import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import LayoutHOC from "./LayoutHOC";

const BASE_URL = process.env.REACT_APP_API_URL || '/';

const BulkImportExport = () => {
  const user = useSelector((state) => state.user);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [loading, setLoading] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAttributeTypes();
  }, []);

  const fetchAttributeTypes = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/bulk/types`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      const data = await response.json();
      if (data.success) {
        setAttributeTypes(data.types);
      }
    } catch (error) {
      console.error("Error fetching attribute types:", error);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedType) return;
    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}api/bulk/export/${selectedType}`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedType}_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting:", error);
    }
    setLoading(false);
  };

  const handleDownloadTemplate = async () => {
    if (!selectedType) return;
    try {
      const response = await fetch(`${BASE_URL}api/bulk/template/${selectedType}`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedType}_template.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        alert('Please upload a CSV file only');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedType || !selectedFile) return;
    setLoading(true);
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('overwrite', overwrite ? '1' : '0');

      const response = await fetch(`${BASE_URL}api/bulk/import/${selectedType}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        body: formData,
      });
      const result = await response.json();
      setResult(result);
      if (result.success) {
        fetchAttributeTypes();
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (error) {
      console.error("Error importing:", error);
      setResult({ success: false, error: error.message });
    }
    setLoading(false);
  };

  const selectedTypeInfo = attributeTypes.find(t => t.key === selectedType);

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
            <i className="bi bi-arrow-left-right text-white text-2xl"></i>
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              Bulk Import / Export
            </h1>
            <p className="text-gray-500 mt-1">Manage attribute data in bulk with CSV format</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <i className="bi bi-collection text-indigo-500"></i>
              Select Data Type
            </h2>
            
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {attributeTypes.map((type) => (
                <button
                  key={type.key}
                  onClick={() => setSelectedType(type.key)}
                  className={`w-full p-4 rounded-xl text-left transition-all duration-200 border-2 ${
                    selectedType === type.key
                      ? 'border-indigo-500 bg-indigo-50 shadow-md'
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className={`font-medium ${selectedType === type.key ? 'text-indigo-700' : 'text-gray-700'}`}>
                        {type.name}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {type.displayFields?.join(' • ')}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      selectedType === type.key
                        ? 'bg-indigo-500 text-white'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {type.count}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <i className="bi bi-download text-emerald-600 text-xl"></i>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Export Data</h2>
                <p className="text-sm text-gray-500">Download records as CSV file</p>
              </div>
            </div>

            {selectedTypeInfo && (
              <div className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600 font-medium">Selected: {selectedTypeInfo.name}</p>
                    <p className="text-2xl font-bold text-emerald-700">{selectedTypeInfo.count} records</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Columns:</p>
                    <p className="text-sm text-gray-600">{selectedTypeInfo.displayFields?.join(', ')}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleExportCSV}
                disabled={!selectedType || loading}
                className="flex-1 min-w-[200px] px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl hover:from-emerald-600 hover:to-teal-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-medium"
              >
                <i className="bi bi-file-earmark-spreadsheet"></i>
                Export as CSV
              </button>
              <button
                onClick={handleDownloadTemplate}
                disabled={!selectedType}
                className="flex-1 min-w-[200px] px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 font-medium"
              >
                <i className="bi bi-file-earmark-arrow-down"></i>
                Download Template
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <i className="bi bi-upload text-blue-600 text-xl"></i>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">Import Data</h2>
                <p className="text-sm text-gray-500">Upload CSV file to add or update records</p>
              </div>
            </div>

            <div className="space-y-5">
              <div 
                className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                  selectedFile 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="p-3 bg-blue-100 rounded-full">
                      <i className="bi bi-file-earmark-check text-blue-600 text-2xl"></i>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-800">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="ml-4 p-2 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <i className="bi bi-x-lg text-red-500"></i>
                    </button>
                  </div>
                ) : (
                  <>
                    <i className="bi bi-cloud-arrow-up text-4xl text-gray-400 mb-3"></i>
                    <p className="text-gray-600 font-medium">Drop your CSV file here</p>
                    <p className="text-sm text-gray-400 mt-1">or click to browse</p>
                  </>
                )}
              </div>

              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <input
                  type="checkbox"
                  id="overwrite"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                  className="w-5 h-5 text-amber-500 border-2 border-amber-300 rounded focus:ring-amber-400"
                />
                <label htmlFor="overwrite" className="flex-1">
                  <span className="font-medium text-amber-800">Overwrite existing records</span>
                  <p className="text-sm text-amber-600">Update existing entries with matching names</p>
                </label>
              </div>

              <button
                onClick={handleImport}
                disabled={!selectedType || !selectedFile || loading}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 font-semibold text-lg"
              >
                {loading ? (
                  <>
                    <i className="bi bi-arrow-repeat animate-spin"></i>
                    Importing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-upload"></i>
                    Import Data
                  </>
                )}
              </button>
            </div>
          </div>

          {result && (
            <div className={`rounded-2xl p-6 border-2 ${
              result.success 
                ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200' 
                : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2 rounded-full ${result.success ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  <i className={`bi ${result.success ? 'bi-check-circle text-emerald-600' : 'bi-x-circle text-red-600'} text-xl`}></i>
                </div>
                <h3 className={`font-semibold text-lg ${result.success ? 'text-emerald-800' : 'text-red-800'}`}>
                  {result.success ? 'Import Completed Successfully!' : 'Import Failed'}
                </h3>
              </div>
              
              {result.success && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-xl text-center shadow-sm">
                    <p className="text-3xl font-bold text-emerald-600">{result.imported}</p>
                    <p className="text-sm text-gray-500 mt-1">New Records</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl text-center shadow-sm">
                    <p className="text-3xl font-bold text-blue-600">{result.updated}</p>
                    <p className="text-sm text-gray-500 mt-1">Updated</p>
                  </div>
                  <div className="bg-white p-4 rounded-xl text-center shadow-sm">
                    <p className="text-3xl font-bold text-gray-400">{result.skipped}</p>
                    <p className="text-sm text-gray-500 mt-1">Skipped</p>
                  </div>
                </div>
              )}
              
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4 p-4 bg-white rounded-xl">
                  <p className="font-medium text-red-700 mb-2">Errors:</p>
                  <ul className="space-y-1 text-sm text-red-600">
                    {result.errors.slice(0, 5).map((err, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <i className="bi bi-exclamation-circle mt-0.5"></i>
                        {err}
                      </li>
                    ))}
                    {result.errors.length > 5 && (
                      <li className="text-gray-500">...and {result.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </main>
  );
};

export default LayoutHOC(BulkImportExport);
